import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Plus, Tags } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { useTagsets, useBulkDeleteTagsets, Tagset } from '@/hooks/useTagsets'
import { DataTable } from '@/components/common/DataTable'
import { useToast } from '@/hooks/use-toast'

const statusBadge = (status: Tagset['status']) => {
  switch (status) {
    case 'DONE':
      return <Badge variant="default" className="bg-green-600">Ready</Badge>
    case 'RUNNING':
      return <Badge variant="secondary" className="animate-pulse">Processing</Badge>
    case 'FAIL':
      return <Badge variant="destructive">Failed</Badge>
    default:
      return <Badge variant="outline">Not Started</Badge>
  }
}

const sourceLabel = (source: Tagset['source_type']) => {
  switch (source) {
    case 'genclair': return 'GenClair'
    case 'upload': return 'File Upload'
    default: return 'Empty'
  }
}

const columns: ColumnDef<Tagset, any>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <Link
        to={`/app/tagsets/${row.original.id}`}
        className="font-medium text-primary hover:underline"
      >
        {row.original.name}
      </Link>
    ),
  },
  {
    accessorKey: 'description',
    header: 'Description',
    cell: ({ row }) => (
      <span className="text-muted-foreground line-clamp-1 max-w-[250px]">
        {row.original.description || '—'}
      </span>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => statusBadge(row.original.status),
  },
  {
    accessorKey: 'source_type',
    header: 'Source',
    cell: ({ row }) => sourceLabel(row.original.source_type),
  },
  {
    accessorKey: 'tag_count',
    header: 'Tags',
    cell: ({ row }) => row.original.tag_count,
  },
  {
    accessorKey: 'linked_kb_count',
    header: 'Linked KBs',
    cell: ({ row }) => row.original.linked_kb_count,
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) =>
      formatDistanceToNow(new Date(row.original.created_at), { addSuffix: true }),
  },
]

export function TagsetsList() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState('')

  const { data, isLoading } = useTagsets({ page, pageSize, search })
  const bulkDelete = useBulkDeleteTagsets()
  const { toast } = useToast()

  const handleBulkDelete = (ids: string[]) => {
    bulkDelete.mutate(ids, {
      onSuccess: (res) => {
        toast({ title: `Deleted ${res.deleted_count} tagset(s)` })
      },
      onError: () => {
        toast({ title: 'Failed to delete', variant: 'destructive' })
      },
    })
  }

  const emptyState = (
    <Card className="border-dashed border-0 shadow-none">
      <CardContent className="flex flex-col items-center justify-center py-16">
        <Tags className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No Tagsets Yet</h3>
        <p className="text-muted-foreground text-center max-w-md mb-6">
          Tagsets are structured tag datasets that enhance retrieval accuracy.
          Create one by uploading a CSV file or generating from a GenClair taxonomy run.
        </p>
        <Button asChild>
          <Link to="/app/tagsets/create">
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Tagset
          </Link>
        </Button>
      </CardContent>
    </Card>
  )

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Tagsets</h1>
          <p className="text-muted-foreground mt-2">
            Manage reusable tag datasets that enhance retrieval across knowledge bases
          </p>
        </div>
        <Button asChild>
          <Link to="/app/tagsets/create">
            <Plus className="h-4 w-4 mr-2" />
            Create Tagset
          </Link>
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        search={search}
        isLoading={isLoading}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSearchChange={setSearch}
        onBulkDelete={handleBulkDelete}
        isBulkDeleting={bulkDelete.isPending}
        getRowId={(row) => row.id}
        emptyState={emptyState}
      />
    </div>
  )
}
